import { CORRUPTION_CATEGORIES, type CorruptionCategory } from '../types';

/**
 * Corruption detection utilities
 * 
 * Helpers for parsing and analyzing corruption details from SyncEvent records
 */

export interface CorruptionFinding {
  category: CorruptionCategory;
  description: string;
  raw: string;
}

/**
 * Parse the suspicious_details string into structured findings
 * Format: "CATEGORY: description | CATEGORY: description | ..."
 */
export function parseCorruptionDetails(details: string | null): CorruptionFinding[] {
  if (!details) return [];

  return details.split(' | ').map((finding) => {
    const colonIndex = finding.indexOf(':');
    if (colonIndex === -1) {
      return {
        category: 'ENCODING_GARBAGE' as CorruptionCategory,
        description: finding,
        raw: finding,
      };
    }

    const category = finding.substring(0, colonIndex).trim() as CorruptionCategory;
    const description = finding.substring(colonIndex + 1).trim();

    return {
      category,
      description,
      raw: finding,
    };
  });
}

/**
 * Get severity level based on corruption findings
 */
export function getCorruptionSeverity(
  findings: CorruptionFinding[],
  charCount: number | null
): 'low' | 'medium' | 'high' | 'critical' {
  if (findings.length === 0) return 'low';

  // TLS cert data indicates network-layer issue - always high
  if (findings.some((f) => f.category === CORRUPTION_CATEGORIES.TLS_CERT_DATA)) {
    return 'critical';
  }

  // Multiple categories = worse
  if (findings.length >= 3) return 'critical';
  if (findings.length >= 2) return 'high';

  // High character count
  if (charCount && charCount > 1000) return 'high';
  if (charCount && charCount > 100) return 'medium';

  return 'low';
}

/**
 * Get human-readable description for corruption category
 */
export function getCategoryDescription(category: CorruptionCategory): string {
  const descriptions: Record<CorruptionCategory, string> = {
    [CORRUPTION_CATEGORIES.NULL_BYTES]: 'Null bytes detected in text content',
    [CORRUPTION_CATEGORIES.CONTROL_CHARS]: 'Non-printable control characters found',
    [CORRUPTION_CATEGORIES.TLS_CERT_DATA]: 'TLS/SSL certificate data leaked into content',
    [CORRUPTION_CATEGORIES.ENCODING_GARBAGE]: 'Extended ASCII encoding corruption',
    [CORRUPTION_CATEGORIES.REPEATED_NULL_DISPLAY]: 'Repeated null byte display pattern',
    [CORRUPTION_CATEGORIES.REPEATED_AT]: 'Excessive @ character repetition',
    [CORRUPTION_CATEGORIES.REPEATED_PERCENT]: 'Excessive % character repetition',
    [CORRUPTION_CATEGORIES.REPEATED_QUESTION]: 'Excessive ? character repetition',
    [CORRUPTION_CATEGORIES.MIXED_GARBAGE]: 'Binary garbage mixed with readable text',
  };

  return descriptions[category] || 'Unknown corruption type';
}

/**
 * Get severity color for UI display
 */
export function getSeverityColor(severity: ReturnType<typeof getCorruptionSeverity>): string {
  const colors = {
    low: '#f59e0b',      // amber
    medium: '#f97316',   // orange
    high: '#ef4444',     // red
    critical: '#dc2626', // dark red
  };
  return colors[severity];
}

